// src/pages/ContactPage.jsx
//
// Layout (top to bottom), each fact appears exactly once:
//   1. Hero        - headline, one-line pitch, "Request a Quote" + "Call Us".
//   2. Quote block - the enquiry form on the left; a single "Contact details"
//                    card on the right holding address, phone/WhatsApp, email,
//                    hours, what to expect, and social links.
//   3. Map block   - Google Map of the Gurugram facility + a Google review link.
//   4. Newsletter  - email sign-up, same form as the site footer.
import React from "react";
import "./ContactPage.css";
import EnquiryForm from "./EnquiryForm";
import NewsletterSignup from "../components/NewsletterSignup";
import contactHeroImg from "./ContactHeroImage.jpg";
import { reportConversion, reportConversionAndNavigate } from "../utils/googleAdsConversion";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaLinkedin,
  FaInstagram,
  FaYoutube,
  FaWhatsapp,
  FaClock,
  FaArrowRight,
  FaCheck,
  FaExternalLinkAlt,
} from "react-icons/fa";

// ── Data ──────────────────────────────────────────────────────────────────────

const PHONE_DISPLAY = "+91 95610 91506";
const PHONE_TEL = "tel:+919561091506";
const WHATSAPP_URL = "https://wa.me/919561091506";
const EMAIL = "info@bibaelectronics.com";

const MAPS_LINK =
  "https://www.google.com/maps/place/BIBA+Electronics+Pvt+Ltd/@28.4397477,76.9956437,17z/data=!3m1!4b1!4m9!1m2!2m1!1sPlot+357E,+Phase+6,+Udyog+Vihar+Industrial+Area,+Phase+VI,+Sector+37,+Gurugram,+Haryana+122004!3m5!1s0x390d194de9852445:0x9570adf645e056f1!8m2!3d28.4397431!4d77.0005146!16s%2Fg%2F11t0pgwprw?entry=ttu&g_ep=EgoyMDI2MDMyNC4wIKXMDSoASAFQAw%3D%3D";

const MAP_EMBED_SRC =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3509.1!2d76.9979397!3d28.4397431!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d194de9852445%3A0x9570adf645e056f1!2sBIBA%20Electronics%20Pvt%20Ltd!5e0!3m2!1sen!2sin!4v1711800000000!5m2!1sen!2sin";

// Three short reassurances shown under the contact details.
const EXPECT = [
  "Reply within one business day with pricing and lead time",
  "An application engineer on the call, not just sales",
  "Displays, touch and cover glass, EMS and embedded design",
];

const SOCIALS = [
  { icon: <FaLinkedin />, label: "LinkedIn", href: "https://www.linkedin.com/company/biba-electronics-pvt-ltd", color: "#0a66c2" },
  { icon: <FaInstagram />, label: "Instagram", href: "https://www.instagram.com/bibaelectronicspvtltd/", color: "#e1306c" },
  { icon: <FaYoutube />, label: "YouTube", href: "https://www.youtube.com/@BIBAElectronicsPvtLtd", color: "#ff0000" },
  { icon: <FaWhatsapp />, label: "WhatsApp", href: WHATSAPP_URL, color: "#25d366" },
];

// ══════════════════════════════════════════════════════════════════════════════
const ContactPage = () => {
  return (
    <div className="cp-root">

      {/* Breadcrumbs - above the hero, same as the Careers page */}
      <div className="cp-breadcrumb-bar">
        <div className="cp-breadcrumbs">
          <a href="/">Home</a>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Contact</span>
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          1. HERO — same two-column banner as the Careers page.
          Photo: src/pages/ContactHeroImage.jpg (BiBA reception, 3:2, 1536x1024).
      ══════════════════════════════════════════════ */}
      <section className="cp-hero">
        <div className="cp-wrap">
          <div className="cp-hero-banner">
            <div className="cp-hero-text">
              <span className="cp-badge">CONTACT US</span>
              <h1 className="cp-hero-title">
                Let&apos;s Talk About<br />
                <span className="cp-hero-green">Your Next Product.</span>
              </h1>
              <p className="cp-hero-desc">
                Whether you need a <em>TFT or OLED display</em>, a
                <em> Smart UART HMI</em>, <em>PCB assembly</em> or a complete
                <em> embedded design</em>, our engineers and sales team in
                Gurugram are one call away.
              </p>

              <div className="cp-hero-actions">
                <a href="#rfq" className="cp-hero-btn">
                  Request a Quote <FaArrowRight />
                </a>
                <a
                  href={PHONE_TEL}
                  className="cp-hero-btn cp-hero-btn--ghost"
                  onClick={(e) => {
                    e.preventDefault();
                    reportConversionAndNavigate("callClick", PHONE_TEL);
                  }}
                >
                  <FaPhoneAlt /> Call Us
                </a>
              </div>
            </div>

            <div className="cp-hero-image-col">
              <div className="cp-hero-image-card">
                <img
                  src={contactHeroImg}
                  alt="BiBA Electronics reception in Gurugram, with a receptionist ready to take your enquiry"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          2. QUOTE BLOCK — form + the one contact-details card
      ══════════════════════════════════════════════ */}
      <section className="cp-rfq-section" id="rfq">
        <div className="cp-wrap">
          <div className="cp-rfq-grid">

            {/* LEFT — the form (it carries its own heading) */}
            <div className="cp-rfq-form-col">
              <EnquiryForm />
            </div>

            {/* RIGHT — contact details, single source of truth */}
            <aside className="cp-details" aria-labelledby="cp-details-title">
              <h2 className="cp-details-title" id="cp-details-title">Contact details</h2>

              <ul className="cp-details-list">
                <li className="cp-details-row">
                  <span className="cp-details-icon"><FaMapMarkerAlt /></span>
                  <div className="cp-details-body">
                    <span className="cp-details-label">Address</span>
                    <p className="cp-details-value">
                      Plot 357E, Phase 6, Udyog Vihar<br />
                      Industrial Area, Sector 37<br />
                      Gurugram, Haryana 122004
                    </p>
                    <a
                      href={MAPS_LINK}
                      className="cp-details-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => reportConversion("getDirections")}
                    >
                      Open in Google Maps <FaExternalLinkAlt />
                    </a>
                  </div>
                </li>

                <li className="cp-details-row">
                  <span className="cp-details-icon"><FaPhoneAlt /></span>
                  <div className="cp-details-body">
                    <span className="cp-details-label">Phone &amp; WhatsApp</span>
                    <p className="cp-details-value">
                      <a
                        href={PHONE_TEL}
                        className="cp-details-strong"
                        onClick={(e) => {
                          e.preventDefault();
                          reportConversionAndNavigate("callClick", PHONE_TEL);
                        }}
                      >
                        {PHONE_DISPLAY}
                      </a>
                    </p>
                    <div className="cp-details-links">
                      <a
                        href={PHONE_TEL}
                        className="cp-details-link"
                        onClick={(e) => {
                          e.preventDefault();
                          reportConversionAndNavigate("callClick", PHONE_TEL);
                        }}
                      >
                        <FaPhoneAlt /> Call
                      </a>
                      <a
                        href={WHATSAPP_URL}
                        className="cp-details-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => reportConversion("whatsappClick")}
                      >
                        <FaWhatsapp /> WhatsApp
                      </a>
                    </div>
                  </div>
                </li>

                <li className="cp-details-row">
                  <span className="cp-details-icon"><FaEnvelope /></span>
                  <div className="cp-details-body">
                    <span className="cp-details-label">Email</span>
                    <p className="cp-details-value">
                      <a href={`mailto:${EMAIL}`} className="cp-details-strong">{EMAIL}</a>
                    </p>
                  </div>
                </li>

                <li className="cp-details-row">
                  <span className="cp-details-icon"><FaClock /></span>
                  <div className="cp-details-body">
                    <span className="cp-details-label">Business hours</span>
                    <p className="cp-details-value">
                      Monday – Saturday<br />
                      9:30 AM – 6:30 PM IST
                    </p>
                  </div>
                </li>
              </ul>

              <div className="cp-details-divider" />

              <span className="cp-details-label">What to expect</span>
              <ul className="cp-expect">
                {EXPECT.map((line) => (
                  <li key={line}>
                    <FaCheck className="cp-expect-icon" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>

              <div className="cp-details-divider" />

              <span className="cp-details-label">Follow BiBA</span>
              <div className="cp-socials">
                {SOCIALS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    className="cp-social"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    title={s.label}
                    style={{ "--social-color": s.color }}
                    onClick={s.label === "WhatsApp" ? () => reportConversion("whatsappClick") : undefined}
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </aside>

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          3. MAP BLOCK — where we are + review link
      ══════════════════════════════════════════════ */}
      <section className="cp-map-section">
        <div className="cp-wrap">
          <div className="cp-map-head">
            <span className="cp-badge">FIND US</span>
            <h2 className="cp-map-title">Visit us in Gurugram</h2>
            <p className="cp-map-desc">
              Our design center and manufacturing facility are at Udyog Vihar
              Phase 6, a 20-minute drive from IGI Airport. Drop in during
              business hours or book a slot with us first.
            </p>
          </div>

          <div className="cp-map-container">
            <iframe
              title="BiBA Electronics Location"
              src={MAP_EMBED_SRC}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>

          <div className="cp-review-strip">
            <div className="cp-review-text">
              <h3 className="cp-review-title">Enjoyed working with us?</h3>
              <p className="cp-review-desc">
                A Google review helps other businesses find us and means a lot to the team.
              </p>
            </div>
            <a
              href={MAPS_LINK}
              className="cp-review-btn"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => reportConversion("getDirections")}
            >
              Write a review <FaArrowRight />
            </a>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          4. NEWSLETTER — same sign-up as the site footer
      ══════════════════════════════════════════════ */}
      <section className="cp-newsletter-section">
        <div className="cp-wrap">
          <div className="cp-newsletter-card">
            <NewsletterSignup
              variant="panel"
              source="contact-page"
              title="Subscribe to our newsletter"
              description="New displays and boards, engineering notes and offers — about once a month, straight to your inbox. No spam, unsubscribe any time."
            />
          </div>
        </div>
      </section>

    </div>
  );
};

export default ContactPage;
